/**
 * @file Client wrapper for the `saveAnalyticsConfig` callable, the only writer
 * of `parameters/analytics` and `parameters/analyticsBindings`: a save of one
 * document's keys or a turn-off, settled to an outcome, never throwing.
 * Consumers: the Settings → Analytics required keys, Teams and bindings
 * sections, and the "Turn analytics off" control. Only an `invalid-argument`
 * rejection whose `details.key` is a string is a refusal at that dotted key;
 * any other rejection fails with its message.
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/services/firebaseFunctions';
import type { AnalyticsConfigSaveOutcome } from '@/analytics';
import type { SaveAnalyticsConfigRequest } from '#types';
import { readCallableError } from '@/workflows/runtime/readCallableError';

const refusedKeyOf = (error: unknown): string | undefined =>
  error instanceof Error &&
  'details' in error &&
  typeof error.details === 'object' &&
  error.details !== null &&
  'key' in error.details &&
  typeof error.details.key === 'string'
    ? error.details.key
    : undefined;

export const saveAnalyticsConfig = async (
  request: SaveAnalyticsConfigRequest,
): Promise<AnalyticsConfigSaveOutcome> => {
  try {
    const callable = httpsCallable<SaveAnalyticsConfigRequest, unknown>(
      functions,
      'saveAnalyticsConfig',
    );
    await callable(request);
    return { status: 'done' };
  } catch (error) {
    const { code, message } = readCallableError(error);
    const key = refusedKeyOf(error);
    if (code === 'invalid-argument' && key !== undefined)
      return { status: 'refused', key, message };
    return { status: 'failed', message: message || 'The save failed.' };
  }
};
